import React, { useEffect, useState } from "react";
import Leftsidebar from "../../sidebar/leftsidebar";
import LeftResponsivesidebar from "../../sidebar/left-responsive";
import Headmenu from "../../sidebar/headmenu";
import BillingBackButton from "./BillingBackButton";
import availableBalanceIcon from "../../../assets/images/icons/billing/available-balance.svg";
import currentSpendIcon from "../../../assets/images/icons/billing/current-spend.svg";
import accountCapIcon from "../../../assets/images/icons/billing/account-cap.svg";
import checkCircleIcon from "../../../assets/images/icons/billing/check-circle.svg";
import calendarIcon from "../../../assets/images/icons/calendar.svg";
import { listInvoices, getInvoiceDetail, getInvoicePdfBlob } from "../../../hook/useAdsApi";

const PAGE_LIMIT = 10;

// No SVG assets were provided for these two (unlike the other billing icons), so they're small
// inline glyphs matching the eye/download icons shown in the reference screenshots.
function EyeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 3v12m0 0l-4-4m4 4l4-4M4 19h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// No storefront/card SVG assets exist in this billing icon set, so these follow the same
// hand-rolled inline-glyph convention as EyeIcon/DownloadIcon above.
function StoreIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 10l1-5h14l1 5M4 10a2 2 0 004 0 2 2 0 004 0 2 2 0 004 0 2 2 0 004 0M4 10v9a1 1 0 001 1h5v-6h4v6h5a1 1 0 001-1v-9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CardIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="28" height="28" rx="7" fill="#2563eb" />
      <rect x="5" y="9" width="18" height="12" rx="2.5" fill="#eff6ff" />
      <rect x="5" y="12" width="18" height="3" fill="#2563eb" />
      <rect x="7" y="17" width="6" height="2" rx="1" fill="#2563eb" />
    </svg>
  );
}

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" }) : "—";

// The PDF is generated on-demand by an authenticated route (getInvoicePdfBlob sends the auth
// token header), not read from a stored file/URL - a plain <a href> can't attach that header, so
// both View and Download fetch the blob first and hand it to the browser client-side.
async function viewInvoicePdf(invoiceId) {
  const blob = await getInvoicePdfBlob(invoiceId);
  const blobUrl = URL.createObjectURL(blob);
  window.open(blobUrl, "_blank", "noreferrer");
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
}

async function downloadInvoicePdf(invoiceId, filename) {
  const blob = await getInvoicePdfBlob(invoiceId);
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename || "invoice.pdf";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(blobUrl);
}

// The rest of the billing screens all show amounts as "$ 20.00" (a symbol), never the raw
// currency_code ("USD 20.00") - this page was the one inconsistent spot, showing the code.
const CURRENCY_SYMBOLS = { INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "AED", AUD: "A$", CAD: "C$", SGD: "S$" };
const currencySymbol = (code) => CURRENCY_SYMBOLS[code] || code || "$";

// Builds a compact page-number list with "..." gaps for large page counts, e.g.
// [1, "...", 11, 12, 13, "...", 24] - matches the reference's numbered pagination style.
// (Two separate "..." markers can legitimately appear, so this can't dedupe with a Set.)
function buildPageList(current, totalPages) {
  const pages = [1];
  if (current > 3) pages.push("...");
  for (let p = Math.max(2, current - 1); p <= Math.min(totalPages - 1, current + 1); p += 1) {
    if (!pages.includes(p)) pages.push(p);
  }
  if (current < totalPages - 2) pages.push("...");
  if (totalPages > 1 && pages[pages.length - 1] !== totalPages) pages.push(totalPages);
  return pages;
}

function InvoiceDetailModal({ invoiceId, onClose }) {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!invoiceId) return;
    setLoading(true);
    getInvoiceDetail(invoiceId)
      .then((res) => setInvoice(res))
      .catch(() => setInvoice(null))
      .finally(() => setLoading(false));
  }, [invoiceId]);

  if (!invoiceId) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
        {loading || !invoice ? (
          <p className="text-sm text-gray-500 py-10 text-center">
            {loading ? "Loading invoice..." : "Invoice not found."}
          </p>
        ) : (
          <>
            <div className="flex items-center justify-between pb-4 mb-4 border-b border-gray-100">
              <h2 className="text-lg font-bold">Invoice Details</h2>
              <button
                type="button"
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-[#095228] text-white flex items-center justify-center"
              >
                ✕
              </button>
            </div>

            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 shrink-0 rounded-full bg-[#ecfbf3] text-[#095228] flex items-center justify-center">
                  <StoreIcon />
                </div>
                <div>
                  <p className="font-bold">{invoice.snapshot_billing_name || "—"}</p>
                  {/* Snapshotted at generation time - snapshot_tax_id on AdInvoice, never the live
                      profile, so a later tax-profile edit never changes this. */}
                  <p className="text-xs text-gray-500">Tax No: {invoice.snapshot_tax_id || "—"}</p>
                </div>
              </div>
              <div className="text-right">
                <span className="inline-flex items-center gap-1 text-xs bg-[#ecfbf3] text-[#095228] rounded-full px-3 py-1 capitalize">
                  <img src={checkCircleIcon} alt="" className="w-3.5 h-3.5" />
                  {invoice.payment_status}
                </span>
                <p className="text-xs text-gray-500 mt-1">Invoice #{invoice.invoice_number}</p>
                <p className="text-xs text-gray-400">Issue Date: {formatDate(invoice.created_at)}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 divide-x divide-gray-200 bg-gray-50 rounded-xl p-3 mb-4">
              <div className="flex items-center gap-2.5">
                <img src={calendarIcon} alt="" className="w-6 h-6 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Billing Period</p>
                  <p className="text-sm font-semibold mt-0.5">
                    {formatDate(invoice.billing_period_start)} - {formatDate(invoice.billing_period_end)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2.5 pl-3">
                <CardIcon />
                <div>
                  <p className="text-xs text-gray-500">Payment Method</p>
                  <p className={`text-sm font-semibold mt-0.5 ${invoice.snapshot_payment_method ? "" : "text-gray-400"}`}>
                    {invoice.snapshot_payment_method || "Not available"}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-between text-sm font-semibold text-gray-500 mb-2">
              <span>Description</span>
              <span>Amount</span>
            </div>
            {(invoice.lineItems || []).map((item) => (
              <div key={item.id} className="flex justify-between text-sm mb-1">
                <span>{item.description}</span>
                <span>{currencySymbol(invoice.currency_code)} {Number(item.amount).toFixed(2)}</span>
              </div>
            ))}
            <div className="flex justify-between text-sm mb-3">
              <span>{invoice.tax_type?.toUpperCase() || "Tax"} ({invoice.tax_rate}%)</span>
              <span>{currencySymbol(invoice.currency_code)} {Number(invoice.tax_amount).toFixed(2)}</span>
            </div>

            <div className="flex justify-between items-center bg-[#ecfbf3] rounded-xl px-4 py-3 mb-4">
              <span className="font-semibold text-sm">Total Amount</span>
              <span className="font-black text-lg">{currencySymbol(invoice.currency_code)} {Number(invoice.total_amount).toFixed(2)}</span>
            </div>

            {/* "View Invoice" here opens the actual generated PDF file in a new tab (viewing the
                document itself) - a different action from the list row's "View Invoice", which
                opens this modal (a details summary). "Download" always forces a real file save. */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => viewInvoicePdf(invoice.id)}
                className="flex-1 flex items-center justify-center gap-2 border border-gray-300 rounded-full py-2.5 text-sm font-semibold"
              >
                <EyeIcon />
                View Invoice
              </button>
              <button
                type="button"
                onClick={() => downloadInvoicePdf(invoice.id, `${invoice.invoice_number}.pdf`)}
                className="flex-1 flex items-center justify-center gap-2 bg-[#095228] text-white rounded-full py-2.5 text-sm font-semibold"
              >
                <DownloadIcon />
                Download Invoice PDF
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function AdInvoices() {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [status, setStatus] = useState("");
  const [currencyCode, setCurrencyCode] = useState("");
  // Applied on "Apply" click, not on every keystroke - avoids refetching mid-typing and matches
  // the filter row's existing "Apply" button UX.
  const [appliedFilters, setAppliedFilters] = useState({});
  const [totals, setTotals] = useState({ total_invoiced: null, total_paid: null, outstanding: null });

  useEffect(() => {
    setLoading(true);
    listInvoices({ page, limit: PAGE_LIMIT, ...appliedFilters })
      .then((res) => {
        setInvoices(res?.invoices || []);
        setTotal(res?.total || 0);
        setTotals({
          total_invoiced: res?.total_invoiced ?? null,
          total_paid: res?.total_paid ?? null,
          outstanding: res?.outstanding ?? null,
        });
      })
      .catch(() => setInvoices([]))
      .finally(() => setLoading(false));
  }, [page, appliedFilters]);

  const handleApplyFilters = () => {
    setPage(1);
    setAppliedFilters({
      status: status || undefined,
      currency_code: currencyCode || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    });
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));

  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />
      <main className="flex-1 p-6 pl-8">
        <LeftResponsivesidebar />
        <Headmenu />
        <main className="flex">
          <div className="w-full max-w-5xl">
            <div className="mb-6 flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
                <p className="text-sm text-gray-500 mt-1">View and download your invoices for all advertising spend.</p>
                <div className="border-b border-gray-200 mt-2 w-[420px]" />
              </div>
              <BillingBackButton />
            </div>

            {/* Date Range/Status/Currency now filter server-side via GET /billing/invoices -
                "Billing Period" dropped (no equivalent backend concept beyond the date range). */}
            <div className="rounded-2xl border border-gray-200 p-4 mb-5 flex gap-3 items-end flex-wrap">
              <div>
                <label className="block text-xs text-gray-500 mb-1">From</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">To</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                >
                  <option value="">All Status</option>
                  <option value="paid">Paid</option>
                  <option value="unpaid">Unpaid</option>
                  <option value="partial">Partial</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Currency</label>
                <select
                  value={currencyCode}
                  onChange={(e) => setCurrencyCode(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                >
                  <option value="">All Currencies</option>
                  {["INR", "USD", "EUR", "GBP", "AED", "AUD", "CAD", "SGD"].map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={handleApplyFilters}
                className="bg-[#095228] text-white rounded-lg px-4 py-1.5 text-sm font-semibold hover:opacity-90 transition"
              >
                Apply
              </button>
            </div>

            {/* Stat cards - now backed by real aggregates from GET /billing/invoices (summed
                server-side across all matching invoices, not just the current page). */}
            <div className="grid grid-cols-3 gap-4 mb-5">
              <div className="rounded-2xl p-4 bg-[#fefdeb] flex items-center gap-3">
                <img src={availableBalanceIcon} alt="" className="w-10 h-10" />
                <div>
                  <p className="text-2xl font-black">$ {Number(totals.total_invoiced || 0).toLocaleString()}</p>
                  <p className="text-sm text-gray-600">Total Invoiced</p>
                </div>
              </div>
              <div className="rounded-2xl p-4 bg-[#ebf8ed] flex items-center gap-3">
                <img src={currentSpendIcon} alt="" className="w-10 h-10" />
                <div>
                  <p className="text-2xl font-black">$ {Number(totals.total_paid || 0).toLocaleString()}</p>
                  <p className="text-sm text-[#095228]">Total Paid</p>
                </div>
              </div>
              <div className="rounded-2xl p-4 bg-[#fdeaea] flex items-center gap-3">
                <img src={accountCapIcon} alt="" className="w-10 h-10" />
                <div>
                  <p className="text-2xl font-black">$ {Number(totals.outstanding || 0).toLocaleString()}</p>
                  <p className="text-sm text-[#a33]">Outstanding</p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="text-left px-4 py-3">#</th>
                    <th className="text-left px-4 py-3">Invoice</th>
                    <th className="text-left px-4 py-3">Billing Period</th>
                    <th className="text-left px-4 py-3">Amount</th>
                    <th className="text-left px-4 py-3">Tax</th>
                    <th className="text-left px-4 py-3">After Tax</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-6 text-center text-gray-500">Loading invoices...</td>
                    </tr>
                  ) : invoices.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-6 text-center text-gray-500">No invoices yet.</td>
                    </tr>
                  ) : (
                    invoices.map((invoice, index) => (
                      <tr key={invoice.id} className="border-t border-gray-100">
                        <td className="px-4 py-3">{(page - 1) * PAGE_LIMIT + index + 1}</td>
                        <td className="px-4 py-3">{invoice.invoice_number}</td>
                        <td className="px-4 py-3">{formatDate(invoice.billing_period_start)}</td>
                        <td className="px-4 py-3">{currencySymbol(invoice.currency_code)} {Number(invoice.subtotal).toFixed(2)}</td>
                        <td className="px-4 py-3">{currencySymbol(invoice.currency_code)} {Number(invoice.tax_amount).toFixed(2)}</td>
                        <td className="px-4 py-3">{currencySymbol(invoice.currency_code)} {Number(invoice.total_amount).toFixed(2)}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`flex items-center gap-1.5 w-fit text-xs rounded-full px-2.5 py-1 capitalize ${
                              invoice.payment_status === "paid" ? "bg-[#ecfbf3] text-[#095228]" : "bg-[#fdeaea] text-[#a33]"
                            }`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${invoice.payment_status === "paid" ? "bg-[#095228]" : "bg-[#a33]"}`} />
                            {invoice.payment_status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2 flex-nowrap">
                            <button
                              type="button"
                              onClick={() => setSelectedInvoiceId(invoice.id)}
                              className="flex items-center gap-1.5 whitespace-nowrap shrink-0 text-xs border border-gray-300 rounded-full px-3 py-1.5 font-semibold"
                            >
                              <EyeIcon />
                              View Invoice
                            </button>
                            {/* Same outline style as View Invoice, not a green fill - matches the
                                reference exactly (both actions read as equal-weight here). */}
                            <button
                              type="button"
                              onClick={() => downloadInvoicePdf(invoice.id, `${invoice.invoice_number}.pdf`)}
                              className="flex items-center gap-1.5 whitespace-nowrap shrink-0 text-xs border border-gray-300 rounded-full px-3 py-1.5 font-semibold"
                            >
                              <DownloadIcon />
                              Download PDF
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Right-aligned only - the reference screenshot showed a duplicate pagination
                control on both sides, which was a mockup mistake, not something to replicate. */}
            {totalPages > 1 && (
              <div className="flex items-center justify-end gap-1.5 mt-4">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 disabled:opacity-40"
                >
                  ‹
                </button>
                {buildPageList(page, totalPages).map((p, i) =>
                  p === "..." ? (
                    <span key={`gap-${i}`} className="w-8 h-8 flex items-center justify-center text-sm text-gray-400">…</span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPage(p)}
                      className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-semibold ${
                        p === page ? "bg-[#095228] text-white" : "border border-gray-300 text-gray-700"
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 disabled:opacity-40"
                >
                  ›
                </button>
              </div>
            )}
          </div>
        </main>
      </main>

      <InvoiceDetailModal invoiceId={selectedInvoiceId} onClose={() => setSelectedInvoiceId(null)} />
    </div>
  );
}
